import React, { useEffect, useState } from 'react';
import {
    Box, Grid, CssBaseline, ThemeProvider, useMediaQuery, Drawer,
} from '@mui/material';
import Zoom from '@mui/material/Zoom';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import theme from './components/Theme';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import RecommendedTips from './components/RecommendedTips';
import TicketList from './components/TicketList';
import RecentTickets from './components/RecentTickets';
import TICKET_MANAGER from './ticket_manager';

import { UPDATE_NULL_IDENTIFIERS } from '../main_components/methods_v2/auxiliary_functions_new_method';
import { get_supports } from '../services/supports/supports';
import { verifyToken } from '../main_components/tokens/verifyToken';
import type { SUPPORTS } from "./interface/interface";

const SUPPORTS_TS: React.FC = () => {
    const [data, setData] = useState<SUPPORTS[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [modalT, setmodalT] = useState<boolean>(false);
    const [showTicketManager, setShowTicketManager] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [managerKey, setManagerKey] = useState(0);

    const isDesktop = useMediaQuery('(min-width:1300px)');
    const showRecommendedTips = useMediaQuery('(min-width:1020px)');

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const update_identifiers = [
        { id: "string" },
        { date: "string" },
        { subject: "string" },
        { severity: "string" },
        { folio: "string" },
        { ticket: "string" },
        { type: "string" },
        { company_code: "string" },
        { user_email: "string" },
        { submodule: "string" },
        { last_update: "string" },
        { user_name: "string" },
        { recurrent_incidence: "string" },
        { note_latest_report: "string" },
        { user_rate: "number" },
        { user_rate_comment: "string" },
    ];

    const fetchSupports = async (): Promise<SUPPORTS[]> => {
        try {
            const rawResponse = await get_supports();
            const cleanedData = rawResponse.data.map((item: any) => ({
                ...item,
                user_rate: (() => {
                    const val = item.user_rate;
                    if (!val || String(val).trim() === "") return null;
                    const num = Number(val);
                    return isNaN(num) ? null : num;
                })(),
            }));

            return UPDATE_NULL_IDENTIFIERS(cleanedData, update_identifiers) as SUPPORTS[];
        } catch (error) {
            console.error("Error al obtener los datos del usuario:", error);
            return [];
        }
    };

    const handleSupports = async (event?: React.SyntheticEvent) => {
        event?.preventDefault();
        setIsLoading(true);
        setmodalT(true);
        setData([]);
        const isTokenValid = await verifyToken();
        if (!isTokenValid) {
            setTimeout(() => {
                handleSupports();
            }, 1000);
            return;
        }
        const supports = await fetchSupports();
        setData(supports);
        setIsLoading(false);
        setmodalT(false);
    };

    useEffect(() => {
        handleSupports();
    }, []);

    useEffect(() => {
        if (showTicketManager) {
            setManagerKey(prev => prev + 1);

            // Forzar resize para redibujar tablas si es necesario
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 350); // espera la animación
        }
    }, [showTicketManager]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />

            {/* Vista Principal */}
            <Zoom in={!showTicketManager} timeout={400}>
                <Box
                    display={showTicketManager ? 'none' : 'flex'}
                    width="100%"
                >
                    {/* ICONO MENÚ SOLO EN PANTALLAS PEQUEÑAS */}
                    {!isDesktop && (
                        <span
                            aria-label="open drawer"
                            onClick={handleDrawerToggle}
                            style={{ paddingTop: 10, cursor: 'pointer' }}
                        >
                            <KeyboardDoubleArrowRightIcon />
                        </span>
                    )}

                    {/* SIDEBAR ESCRITORIO */}
                    {isDesktop && (
                        <Box
                            width="220px"
                            sx={{
                                borderRight: '1px solid #E5E7EB',
                                backgroundColor: '#fff',
                                minHeight: '90vh',
                            }}
                        >
                            <Sidebar />
                        </Box>
                    )}

                    {/* SIDEBAR MÓVIL */}
                    <Drawer
                        anchor="left"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{ keepMounted: true }}
                    >
                        <Box
                            width="280px"
                            sx={{
                                borderRight: '1px solid #E5E7EB',
                                backgroundColor: '#fff',
                                height: '100%',
                            }}
                        >
                            <Sidebar />
                        </Box>
                    </Drawer>

                    <Box flexGrow={1} p={3}>
                        <Header tickets={data} />
                        {showRecommendedTips && <RecommendedTips />}
                        <Grid container spacing={2} mt={2}>
                            <Grid xs={12} md={6}>
                                <TicketList
                                    tickets={data}
                                    onViewAll={() => setShowTicketManager(true)}
                                    fetchSupports={fetchSupports}
                                    flagModal={modalT}
                                    onUpdateTicket={updatedTicket =>
                                        setData(prev =>
                                            prev.map(t =>
                                                t.ticket === updatedTicket.ticket ? updatedTicket : t
                                            )
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xs={12} md={6}>
                                <RecentTickets
                                    tickets={data}
                                    fetchSupports={fetchSupports}
                                    flagModal={modalT}
                                    onUpdateTicket={updatedTicket =>
                                        setData(prev => {
                                            const alreadyExists = prev.some(t => t.ticket === updatedTicket.ticket);
                                            return alreadyExists
                                                ? prev.map(t =>
                                                    t.ticket === updatedTicket.ticket ? updatedTicket : t
                                                )
                                                : [updatedTicket, ...prev];
                                        })
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Zoom>

            {/* Vista Ticket Manager */}
            <Zoom in={showTicketManager} timeout={300}>
                <Box
                    display={!showTicketManager ? 'none' : 'block'}
                    width="100%"
                    p={3}
                >
                    <Header tickets={data} />
                    <TICKET_MANAGER
                        key={managerKey}
                        tickets={data}
                        onViewAll={() => setShowTicketManager(false)}
                        isLoading={true}
                        fetchSupports={fetchSupports}
                        flagModal={modalT}
                        onUpdateTicket={updatedTicket =>
                            setData(prev => {
                                const alreadyExists = prev.some(t => t.ticket === updatedTicket.ticket);
                                return alreadyExists
                                    ? prev.map(t =>
                                        t.ticket === updatedTicket.ticket ? updatedTicket : t
                                    )
                                    : [updatedTicket, ...prev];
                            })
                        }
                    />
                </Box>
            </Zoom>
        </ThemeProvider>
    );
};

export default SUPPORTS_TS;
